import React from "react";
import PropTypes from "prop-types";
// `.btn_div` / `.previous` / `.next` / `.pageindex` are global classes defined in
// src/styles/Instance.scss. Several legacy tables already pull that file in as a
// side effect, but importing it here is what actually guarantees the rules are in
// the bundle — relying on another module's import breaks the moment those pages
// are code-split away from this one.
import "../../styles/Instance.scss";

// Controlled pager: page state lives with the caller. This component only
// renders "Showing a-b of N" plus prev/next buttons and reports the
// requested page via onChange — it never tracks its own page.
//
// `label` overrides the default "Showing a-b of N" text without touching the prev/next
// mechanics. It exists because `total` isn't always the count of what's actually
// rendered: B2B's "All partners" view paginates through the real, server-counted order
// set (so `total`/prev/next are mechanically correct) while CLIENT-side filtering the
// rendered rows down to the ones that have a partner — so the default text would claim
// "Showing 1–20 of 11,742" over a page that visibly renders far fewer rows. A caller in
// that situation supplies its own honest label instead of the default lie.
function Pagination({ page, size, total, onChange, label }) {
    const totalPages = Math.max(Math.ceil(total / size), 1);
    const rangeStart = total === 0 ? 0 : (page - 1) * size + 1;
    const rangeEnd = Math.min(page * size, total);
    const isFirstPage = page <= 1;
    const isLastPage = page >= totalPages;

    // The range label sits BELOW .btn_div rather than inside it: .btn_div is a
    // centred flex row sized for exactly three children (Previous / index / Next),
    // and a fourth child throws that rhythm off.
    // Disabled colours come from Instance.scss's `.previous:disabled` rule — do not
    // add Tailwind disabled: variants here or they will fight it.
    return (
        <>
            <div className="btn_div">
                <button
                    type="button"
                    onClick={() => onChange(page - 1)}
                    disabled={isFirstPage}
                    aria-label="Previous page"
                    className="previous"
                >
                    Previous
                </button>
                <span className="pageindex">{`Page ${page} of ${totalPages}`}</span>
                <button
                    type="button"
                    onClick={() => onChange(page + 1)}
                    disabled={isLastPage}
                    aria-label="Next page"
                    className="next"
                >
                    Next
                </button>
            </div>
            <p className="text-center text-sm text-gray-500 font-sans">
                {label || `Showing ${rangeStart}–${rangeEnd} of ${total}`}
            </p>
        </>
    );
}

Pagination.propTypes = {
    page: PropTypes.number.isRequired,
    size: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.node,
};

Pagination.defaultProps = {
    label: null,
};

export default Pagination;
